import { useEffect, useMemo, useState } from 'react';
import { Megaphone, Send } from 'lucide-react';
import { PageHeader, Section } from '../components/Scaffold';
import useAuthStore from '../stores/authStore';
import api from '../api/client';
import { messageFromDetail } from '../utils/errors';
import { renderBoldText } from '../utils/markdown';

/**
 * Platform announcements.
 *
 * SuperAdmin composes a platform-wide banner that café admins/staff see when
 * they log into primus-admin-main. Backed by a global-DB table
 * (backend/app/db/models_global.py: PlatformAnnouncement) — separate from the
 * per-café app.db.models_cafe.Announcement, which a café's own staff use to
 * notify their own customers and which SuperAdmin has no access to (its
 * token has no cafe_id).
 *
 * Endpoints: POST/GET/PATCH/DELETE /api/internal/announcements[/{id}], POST
 * /api/internal/announcements/{id}/unpublish (superadmin-only).
 */
const STATUS_COLOR = { published: 'success', draft: 'neutral' };
const SEVERITY_OPTIONS = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
];
// Mirrors backend/app/schemas/communication.py PlatformAnnouncementIn.title
// (max_length=6000) — comfortably fits a ~1000-word title.
const MAX_TITLE_LENGTH = 6000;
// Above this length, the "Recent announcements" table clamps the title to 2
// lines with a Show more/Show less toggle — same pattern as the café-side
// banner (primus-admin-main/src/components/PlatformAnnouncementBanner.jsx).
const TITLE_CLAMP_THRESHOLD = 140;

const EMPTY_FORM = {
  title: '',
  content: '',
  severity: 'info',
  audienceScope: 'all',
  targetCafeId: '',
  startTime: '',
  endTime: '',
};

// <input type="datetime-local"> wants LOCAL wall-clock time; the API speaks
// UTC. These two are the only conversions, so a banner's window round-trips
// through an edit unchanged (same as primus-admin-main's CampaignsPage).
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromLocalInput = (s) => (s ? new Date(s).toISOString() : null);
const isExpired = (a) => {
  if (!a.end_time) return false;
  const end = new Date(a.end_time).getTime();
  return !Number.isNaN(end) && end < Date.now();
};

// role in ("platform_owner", "platform_user") — see User.access_level's
// comment in backend/app/models.py for why these Owner/User accounts aren't
// literally "owner"/"user" in the DB.
const PLATFORM_ACCESS_ROLES = ['platform_owner', 'platform_user'];

export default function Announcements() {
  const { isSuperAdmin, user } = useAuthStore();
  const isPlatformRole = PLATFORM_ACCESS_ROLES.includes(user?.role);
  // Viewing existing announcements: any access level, like every other page.
  const canView = isSuperAdmin() || isPlatformRole;
  // Composing/editing/deleting/unpublishing: superadmin, or Write access only.
  const canPublish = isSuperAdmin() || (isPlatformRole && user?.access_level === 'write');

  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cafes, setCafes] = useState([]);
  const [cafesLoaded, setCafesLoaded] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(null); // 'draft' | 'published' | null
  const [formError, setFormError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const fetchAnnouncements = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get('/internal/announcements');
      setAnnouncements(res.data || []);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
      setError(messageFromDetail(err, 'Failed to load announcements'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (canView) fetchAnnouncements();
    else setIsLoading(false);
  }, [canView]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onAudienceChange = async (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, audienceScope: value }));
    if (value === 'single_cafe' && !cafesLoaded) {
      try {
        const res = await api.get('/internal/cafes');
        setCafes(res.data || []);
      } catch (err) {
        console.error('Failed to fetch cafés:', err);
      } finally {
        setCafesLoaded(true);
      }
    }
  };

  const cafeName = useMemo(() => {
    const map = new Map(cafes.map((c) => [c.id, c.name]));
    return (id) => map.get(id) || `Café ${id}`;
  }, [cafes]);

  const publish = async (status) => {
    setFormError('');
    if (!form.title.trim() || !form.content.trim()) {
      setFormError('Title and message are required.');
      return;
    }
    if (form.audienceScope === 'single_cafe' && !form.targetCafeId) {
      setFormError('Pick a café for a single-café announcement.');
      return;
    }
    if (form.startTime && form.endTime && new Date(form.endTime) <= new Date(form.startTime)) {
      setFormError('End date must be after the start date.');
      return;
    }
    setSubmitting(status);
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        severity: form.severity,
        audience_scope: form.audienceScope,
        target_cafe_id: form.audienceScope === 'single_cafe' ? Number(form.targetCafeId) : null,
        status,
        start_time: fromLocalInput(form.startTime),
        end_time: fromLocalInput(form.endTime),
      };
      if (editingId) {
        await api.patch(`/internal/announcements/${editingId}`, payload);
      } else {
        await api.post('/internal/announcements', payload);
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      await fetchAnnouncements();
    } catch (err) {
      console.error('Failed to save announcement:', err);
      setFormError(messageFromDetail(err, 'Failed to save announcement.'));
    } finally {
      setSubmitting(null);
    }
  };

  const unpublish = async (id) => {
    try {
      await api.post(`/internal/announcements/${id}/unpublish`);
      await fetchAnnouncements();
    } catch (err) {
      console.error('Failed to unpublish announcement:', err);
    }
  };

  const startEdit = async (a) => {
    setFormError('');
    setEditingId(a.id);
    setForm({
      title: a.title,
      content: a.content,
      severity: a.severity,
      audienceScope: a.audience_scope,
      targetCafeId: a.target_cafe_id ? String(a.target_cafe_id) : '',
      startTime: toLocalInput(a.start_time),
      endTime: toLocalInput(a.end_time),
    });
    if (a.audience_scope === 'single_cafe' && !cafesLoaded) {
      try {
        const res = await api.get('/internal/cafes');
        setCafes(res.data || []);
      } catch (err) {
        console.error('Failed to fetch cafés:', err);
      } finally {
        setCafesLoaded(true);
      }
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const removeAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await api.delete(`/internal/announcements/${id}`);
      if (editingId === id) cancelEdit();
      await fetchAnnouncements();
    } catch (err) {
      console.error('Failed to delete announcement:', err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="page announcements-page">
      <style>{`
        .announcements-page {
          /* noLag rebrand: scoped to this page only. No Figma node exists
             for Announcements — applied by the same accent-only pattern as
             CafeOnboard/Licenses/Billing rather than verified against a
             design. Severity (info/warning/critical) and status
             (published/draft) badges below are separate --status-*
             tokens, untouched by this. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
        }
      `}</style>
      <PageHeader title="Announcements" subtitle="Broadcast platform banners to café admins" />

      {!canView && (
        <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
          <span>You don&apos;t have access to Announcements.</span>
        </div>
      )}

      {canView && !canPublish && (
        <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
          <span>Read-only access — only Super Admins or Write-access accounts can publish announcements.</span>
        </div>
      )}

      {canPublish && (
        <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
          <Section title={editingId ? 'Edit announcement' : 'Compose announcement'}>
            {formError && (
              <div className="card" style={{ padding: 12, marginBottom: 12, color: 'var(--danger, #f87171)' }}>
                {formError}
              </div>
            )}
            <div className="field">
              <label className="field__label">Title</label>
              <textarea
                className="field__textarea"
                style={{ minHeight: 44 }}
                rows={form.title.length > 120 ? 4 : 1}
                placeholder="Headline — supports long titles (up to ~1000 words)"
                value={form.title}
                maxLength={MAX_TITLE_LENGTH}
                onChange={set('title')}
              />
              <span className="text-tertiary" style={{ fontSize: 12 }}>
                {form.title.length} / {MAX_TITLE_LENGTH} characters
              </span>
            </div>
            <div className="form-grid" style={{ marginTop: 16 }}>
              <div className="field">
                <label className="field__label">Audience</label>
                <select className="field__select" value={form.audienceScope} onChange={onAudienceChange}>
                  <option value="all">All cafés</option>
                  <option value="single_cafe">A single café…</option>
                </select>
              </div>
              {form.audienceScope === 'single_cafe' && (
                <div className="field">
                  <label className="field__label">Café</label>
                  <select className="field__select" value={form.targetCafeId} onChange={set('targetCafeId')}>
                    <option value="">Select café…</option>
                    {cafes.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="field">
                <label className="field__label">Severity</label>
                <select className="field__select" value={form.severity} onChange={set('severity')}>
                  {SEVERITY_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field" style={{ marginTop: 16 }}>
              <label className="field__label">Message</label>
              <textarea
                className="field__textarea"
                placeholder="Body shown in the café admin banner…"
                value={form.content}
                onChange={set('content')}
              />
            </div>
            <div className="form-grid" style={{ marginTop: 16 }}>
              <div className="field">
                <label className="field__label">Starts</label>
                <input className="field__input" type="datetime-local" value={form.startTime} onChange={set('startTime')} />
              </div>
              <div className="field">
                <label className="field__label">Ends</label>
                <input className="field__input" type="datetime-local" value={form.endTime} onChange={set('endTime')} />
              </div>
            </div>
            <span className="text-tertiary" style={{ fontSize: 12 }}>
              Optional. The banner disappears from café admins&apos; dashboards once the end date passes. Leave it empty to keep it until you unpublish it.
            </span>
            <div className="page-actions" style={{ marginTop: 16 }}>
              {editingId && (
                <button className="btn btn--ghost" disabled={!!submitting} onClick={cancelEdit}>
                  Cancel
                </button>
              )}
              <button className="btn btn--secondary" disabled={!!submitting} onClick={() => publish('draft')}>
                {submitting === 'draft' ? 'Saving…' : 'Save draft'}
              </button>
              <button className="btn btn--primary" disabled={!!submitting} onClick={() => publish('published')}>
                <Send size={16} /> {submitting === 'published' ? 'Publishing…' : editingId ? 'Save & publish' : 'Publish'}
              </button>
            </div>
          </Section>
        </div>
      )}

      {canView && (
        <Section title="Recent announcements">
          {isLoading ? (
            <div className="card text-tertiary" style={{ padding: 16 }}>Loading…</div>
          ) : error ? (
            <div className="card text-tertiary" style={{ padding: 16 }}>{error}</div>
          ) : announcements.length === 0 ? (
            <div className="card text-tertiary" style={{ padding: 16 }}>No announcements yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {announcements.map((a) => {
                const isLong = a.title.length > TITLE_CLAMP_THRESHOLD;
                const isExpanded = expandedIds.has(a.id);
                // Past its end time café admins no longer see it (the /active
                // endpoint filters on end_time), so "published" would mislead.
                const expired = a.status === 'published' && isExpired(a);
                return (
                  <div key={a.id} className="card" style={{ padding: 16 }}>
                    {/* Title row: headline reads left-to-right across the full
                        card width, badges pinned top-right — matches how a
                        real announcement is read, instead of squeezing an
                        arbitrarily long title into a narrow table column. */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
                        <Megaphone size={16} style={{ flexShrink: 0, marginTop: 3 }} />
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 15,
                              lineHeight: 1.6,
                              textAlign: 'justify',
                              overflowWrap: 'break-word',
                              // Comfortable reading width — justified text at a
                              // reasonable line length avoids the big uneven word
                              // gaps ("rivers") that show up when you justify very
                              // long or very short lines.
                              maxWidth: '68ch',
                              ...(isLong && !isExpanded
                                ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                                : {}),
                            }}
                          >
                            {renderBoldText(a.title)}
                          </div>
                          {isLong && (
                            <button
                              className="btn btn--ghost btn--sm"
                              style={{ padding: '4px 0', height: 'auto' }}
                              onClick={() => toggleExpanded(a.id)}
                            >
                              {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <span className={`badge badge--${a.severity === 'critical' ? 'danger' : a.severity}`}>{a.severity}</span>
                        <span className={`badge badge--${expired ? 'neutral' : STATUS_COLOR[a.status]}`}>
                          {expired ? 'expired' : a.status}
                        </span>
                      </div>
                    </div>

                    {/* Meta row: who it's for + when, as a compact inline line. */}
                    <div className="text-secondary" style={{ marginTop: 10, fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: 8, paddingLeft: 24 }}>
                      <span>{a.audience_scope === 'all' ? 'All cafés' : cafeName(a.target_cafe_id)}</span>
                      <span className="text-tertiary">·</span>
                      <span className="text-tertiary">{new Date(a.created_at).toLocaleString()}</span>
                      {(a.start_time || a.end_time) && (
                        <>
                          <span className="text-tertiary">·</span>
                          <span className="text-tertiary">
                            {a.start_time ? `From ${new Date(a.start_time).toLocaleString()}` : 'Shown'}
                            {a.end_time ? ` ${expired ? 'ended' : 'until'} ${new Date(a.end_time).toLocaleString()}` : ' with no end date'}
                          </span>
                        </>
                      )}
                    </div>

                    {canPublish && (
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                      {a.status === 'published' && (
                        <button className="btn btn--ghost btn--sm" onClick={() => unpublish(a.id)}>Unpublish</button>
                      )}
                      <button className="btn btn--ghost btn--sm" onClick={() => startEdit(a)}>Edit</button>
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={deletingId === a.id}
                        onClick={() => removeAnnouncement(a.id)}
                      >
                        {deletingId === a.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
